import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { useMessagesStyles } from "./style";
import {
  getBlockedUsersAPI,
  unblockUserAPI,
  getFileByIdAPI,
} from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profileImg?: string;
}

const BlackList: React.FC = () => {
  const classes = useMessagesStyles();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [loadingMedia, setLoadingMedia] = useState<{ [key: string]: boolean }>(
    {},
  );

  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl || mediaUrl === "defaultAvatars") return defaultAvatars;
    if (mediaUrls[mediaUrl]) return mediaUrls[mediaUrl];

    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      setMediaUrls((prev) => ({ ...prev, [mediaUrl]: mediaUrl }));
      setLoadingMedia((prev) => ({ ...prev, [mediaUrl]: false }));
      return mediaUrl;
    }

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls((prev) => ({ ...prev, [mediaUrl]: mediaUrl }));
      setLoadingMedia((prev) => ({ ...prev, [mediaUrl]: false }));
      return mediaUrl;
    }

    setLoadingMedia((prev) => ({ ...prev, [mediaUrl]: true }));
    try {
      const fileId = mediaUrl.split("/api/files/")[1];
      if (!fileId) throw new Error("Invalid file ID");
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls((prev) => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      showToast({ message: "Error fetching avatar", err: true });
      return defaultAvatars;
    } finally {
      setLoadingMedia((prev) => ({ ...prev, [mediaUrl]: false }));
    }
  };

  useEffect(() => {
    const fetchBlockedUsers = async () => {
      setLoading(true);
      try {
        const response = await getBlockedUsersAPI();
        if (response.success) {
          const blockedUsers = response.data.map((item: any) => ({
            _id: item.blockedId._id,
            firstName: item.blockedId.firstName,
            lastName: item.blockedId.lastName,
            username: item.blockedId.username,
            profileImg: item.blockedId.profileImg,
          }));
          setUsers(blockedUsers);
        } else {
          showToast({
            message:
              response.message ||
              "Failed to download the list of blocked users",
            err: true,
          });
        }
      } catch (error) {
        showToast({ message: "Error loading the list", err: true });
        console.error("Error fetching blocked users:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlockedUsers();
  }, []);

  useEffect(() => {
    users.forEach((user) => {
      if (
        user.profileImg &&
        !mediaUrls[user.profileImg] &&
        !loadingMedia[user.profileImg]
      ) {
        fetchMediaUrl(user.profileImg);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const handleUnblockUser = async (userId: string) => {
    try {
      const response = await unblockUserAPI(userId);
      if (response.success) {
        setUsers((prev) => prev.filter((user) => user._id !== userId));
        showToast({ message: "User successfully unlocked", success: true });
      } else {
        showToast({
          message: response.message || "Unable to unlock the user",
          err: true,
        });
      }
    } catch (error) {
      showToast({ message: "Error when unlocking a user", err: true });
      console.error("Unblock user error:", error);
    }
  };

  return (
    <Box className={classes.messagesRoot}>
      <Box className={classes.chatHeader}>
        <Typography className={classes.headerTitle}>Black List</Typography>
      </Box>
      <Box className={classes.chatList} style={{ padding: "16px" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={2}>
            <CircularProgress size={24} />
          </Box>
        ) : users.length === 0 ? (
          <Typography
            style={{ textAlign: "center", color: "#FFFFFF", padding: "20px" }}
          >
            No users on your blacklist
          </Typography>
        ) : (
          users.map((user) => (
            <Box
              key={user._id}
              className={classes.boxUserGroup}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              padding={1}
            >
              <Box display="flex" alignItems="center">
                <Avatar
                  src={mediaUrls[user.profileImg || ""] || defaultAvatars}
                >
                  {loadingMedia[user.profileImg || ""] && (
                    <CircularProgress size={24} />
                  )}
                </Avatar>
                <Box className={classes.nameUserNameGroup}>
                  <Typography
                    style={{
                      marginLeft: 12,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#FFFFFF",
                    }}
                  >
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography
                    style={{
                      marginLeft: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#FE5A97",
                    }}
                  >
                    @{user.username}
                  </Typography>
                </Box>
              </Box>
              <Button
                onClick={() => handleUnblockUser(user._id)}
                className={classes.sendButtonHelp}
                variant="outlined"
                size="small"
              >
                Unlock
              </Button>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default BlackList;
